// ReviewModeManager
// Handles the review mode panel for reviewing pending multimedia items

import { resourcesApi } from '../services/resources-api';

export interface ReviewModeCallbacks {
  onModeChange?: (active: boolean) => void;
  onItemSaved?: (itemId: string) => void;
}

export class ReviewModeManager {
  private reviewModeActive: boolean = false;
  private reviewItems: any[] = [];
  private expandedReviewItemId: string | null = null;
  private callbacks: ReviewModeCallbacks;

  constructor(callbacks: ReviewModeCallbacks = {}) {
    this.callbacks = callbacks;
  }

  isActive(): boolean {
    return this.reviewModeActive;
  }

  async toggle(): Promise<void> {
    const btn = document.getElementById('btn-review-mode');
    this.reviewModeActive = !this.reviewModeActive;

    if (this.reviewModeActive) {
      btn?.classList.add('active');
      await this.loadItems();
      this.showPanel();
    } else {
      btn?.classList.remove('active');
      this.hidePanel();
    }

    this.callbacks.onModeChange?.(this.reviewModeActive);
  }

  private async loadItems(): Promise<void> {
    const response = await resourcesApi.pending(50, 0);
    if (response.success && response.resources) {
      this.reviewItems = response.resources;
    }
  }

  private showPanel(): void {
    let panel = document.getElementById('review-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'review-panel';
      panel.className = 'review-panel';
      document.getElementById('editor-container')?.appendChild(panel);
    }

    panel.innerHTML = '';
    panel.classList.add('visible');

    // Header
    const header = document.createElement('div');
    header.className = 'review-panel-header';
    header.innerHTML = `
      <h3>Review Mode <span style="color: var(--text-muted); font-size: 12px;">(${this.reviewItems.length} items)</span></h3>
      <button id="close-review-panel" style="background: none; border: none; cursor: pointer; padding: 4px;">
        <span class="material-symbols-outlined">close</span>
      </button>
    `;
    header.querySelector('#close-review-panel')?.addEventListener('click', () => {
      this.toggle();
    });
    panel.appendChild(header);

    // Items list
    const list = document.createElement('div');
    list.className = 'review-items-list';

    if (this.reviewItems.length === 0) {
      list.innerHTML = '<div style="padding: 16px; text-align: center; color: var(--text-muted);">No items need review</div>';
    } else {
      for (const item of this.reviewItems) {
        const isExpanded = this.expandedReviewItemId === item.id;
        const itemEl = this.createItemElement(item, isExpanded);
        list.appendChild(itemEl);
      }
    }

    panel.appendChild(list);
  }

  private createItemElement(item: any, isExpanded: boolean): HTMLElement {
    const container = document.createElement('div');
    container.className = 'review-item' + (isExpanded ? ' expanded' : '');

    // Header
    const header = document.createElement('div');
    header.className = 'review-item-header';
    header.innerHTML = `
      <span class="material-symbols-outlined">${isExpanded ? 'expand_less' : 'expand_more'}</span>
      <span class="review-item-title">${item.topic || item.coreConcept || 'No title'}</span>
      <span class="review-item-score" style="color: ${this.getScoreColor(item.completionScore || 0)}">${item.completionScore || 0}%</span>
    `;
    header.addEventListener('click', () => {
      this.expandedReviewItemId = isExpanded ? null : item.id;
      this.showPanel();
    });
    container.appendChild(header);

    // Expanded content
    if (isExpanded) {
      const fields = document.createElement('div');
      fields.className = 'review-item-fields';

      // Topic
      fields.innerHTML = `
        <div class="review-field">
          <label>Topic</label>
          <input type="text" class="review-topic" value="${item.topic || ''}" placeholder="Topic...">
        </div>
      `;

      // Speakers
      if (item.speakers && Object.keys(item.speakers).length > 0) {
        const speakersDiv = document.createElement('div');
        speakersDiv.className = 'review-field';
        speakersDiv.innerHTML = '<label>Speakers</label>';
        for (const [key, value] of Object.entries(item.speakers)) {
          const speakerRow = document.createElement('div');
          speakerRow.className = 'speaker-row';
          speakerRow.innerHTML = `
            <span class="speaker-key">${key}</span>
            <input type="text" class="review-speaker" data-key="${key}" value="${value}" placeholder="Name...">
          `;
          speakersDiv.appendChild(speakerRow);
        }
        fields.appendChild(speakersDiv);
      }

      // Save button
      const actions = document.createElement('div');
      actions.className = 'review-actions';
      actions.innerHTML = '<button class="review-save-btn">Save</button>';
      actions.querySelector('.review-save-btn')?.addEventListener('click', async () => {
        await this.saveItem(item.id, fields);
      });
      fields.appendChild(actions);

      container.appendChild(fields);
    }

    return container;
  }

  private async saveItem(id: string, fieldsEl: HTMLElement): Promise<void> {
    const topicInput = fieldsEl.querySelector('.review-topic') as HTMLInputElement;
    const speakerInputs = fieldsEl.querySelectorAll('.review-speaker') as NodeListOf<HTMLInputElement>;

    const updates: { topic?: string; speakers?: Record<string, string> } = {};

    if (topicInput) {
      updates.topic = topicInput.value;
    }

    if (speakerInputs.length > 0) {
      updates.speakers = {};
      speakerInputs.forEach(input => {
        const key = input.dataset.key;
        if (key) {
          updates.speakers![key] = input.value;
        }
      });
    }

    const response = await resourcesApi.update(id, { metadata: updates });
    if (response.success) {
      await this.loadItems();
      // Move to next item
      const currentIndex = this.reviewItems.findIndex(i => i.id === id);
      if (currentIndex >= 0 && currentIndex < this.reviewItems.length) {
        this.expandedReviewItemId = this.reviewItems[currentIndex]?.id || null;
      } else {
        this.expandedReviewItemId = null;
      }
      this.showPanel();
      this.callbacks.onItemSaved?.(id);
    }
  }

  private hidePanel(): void {
    const panel = document.getElementById('review-panel');
    if (panel) {
      panel.classList.remove('visible');
    }
    this.expandedReviewItemId = null;
  }

  private getScoreColor(score: number): string {
    if (score >= 80) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  }
}
